/**
 * =============================================================================
 * fileCache.js — In-memory cache for file list API responses
 * =============================================================================
 * Mirrors the useCachedData pattern from treatmentpath.
 *
 * Usage in a class component:
 *
 *   import { getCached, setCached, isCacheValid } from '../../../Utils/fileCache';
 *
 *   const CACHE_KEY = 'knowledge-base-files-p1';
 *   const cached = getCached(CACHE_KEY);
 *   if (cached) {
 *     this.setState({ ...cached, loading: false });
 *     // optionally background-refresh
 *   } else {
 *     const data = await fetch();
 *     setCached(CACHE_KEY, snapshot);
 *   }
 * =============================================================================
 */

// Global in-memory store — survives tab navigation within the same session
const cache = new Map();

/** Default TTL: 5 minutes */
const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * Store a value in the cache.
 * @param {string} key
 * @param {*} data
 * @param {number} [ttl] - milliseconds until stale (default 5 min)
 */
export const setCached = (key, data, ttl = DEFAULT_TTL) => {
  cache.set(key, { data, timestamp: Date.now(), ttl });
};

/**
 * Retrieve a cached value if it exists and is not expired.
 * Returns null if missing or stale.
 * @param {string} key
 * @returns {*|null}
 */
export const getCached = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > entry.ttl) {
    cache.delete(key);
    return null;
  }
  return entry.data;
};

/**
 * Delete a specific cache entry (call after mutations like upload/delete).
 * Supports prefix deletion: clearCached('knowledge-base-files') clears all
 * keys that start with that string.
 * @param {string} keyOrPrefix
 */
export const clearCached = (keyOrPrefix) => {
  for (const key of cache.keys()) {
    if (key.startsWith(keyOrPrefix)) {
      cache.delete(key);
    }
  }
};

/** Clear everything (e.g. on logout) */
export const clearAllCache = () => cache.clear();
